import React, { useEffect, useId, useMemo, useRef, useState } from 'react'

const RECENTS_KEY = 'sr_recent_assistance_types_v1'

const stripDiacritics = (value) => {
  try {
    return String(value || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
  } catch {
    return String(value || '')
  }
}

const normalizeText = (value) =>
  stripDiacritics(value)
    .toLowerCase()
    .replace(/_/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()

const safeJsonParse = (value, fallback) => {
  try {
    return JSON.parse(value)
  } catch {
    return fallback
  }
}

const useOutsideClick = (ref, handler, enabled) => {
  useEffect(() => {
    if (!enabled) return

    const onPointerDown = (e) => {
      const el = ref.current
      if (!el) return
      if (el.contains(e.target)) return
      handler?.(e)
    }

    window.addEventListener('pointerdown', onPointerDown, true)
    return () => window.removeEventListener('pointerdown', onPointerDown, true)
  }, [enabled, handler, ref])
}

const flattenOptions = (categories) => {
  const out = []
  for (const cat of categories || []) {
    for (const opt of cat.options || []) {
      out.push({
        category: cat.name,
        value: opt.value,
        label: opt.label,
        search: normalizeText([cat.name, opt.label, opt.value].join(' '))
      })
    }
  }
  return out
}

const readRecents = () => {
  if (typeof window === 'undefined') return []
  const raw = window.localStorage.getItem(RECENTS_KEY)
  const parsed = safeJsonParse(raw, [])
  if (!Array.isArray(parsed)) return []
  return parsed.filter((v) => typeof v === 'string' && v.length < 80).slice(0, 8)
}

const writeRecents = (values) => {
  if (typeof window === 'undefined') return
  window.localStorage.setItem(RECENTS_KEY, JSON.stringify(values))
}

export default function AssistanceTypeCombobox({
  label,
  value,
  onChange,
  categories,
  loading = false,
  required = false,
  disabled = false,
  placeholder = 'Seleccionar...',
  includeAll = false,
  allValue = 'todos',
  allLabel = 'Todos',
  showRecents = true,
  className = '',
  inputClassName = '',
}) {
  const rootRef = useRef(null)
  const searchRef = useRef(null)
  const uid = useId()
  const listboxId = `combobox-listbox-${uid}`
  const optionId = (idx) => `combobox-option-${uid}-${idx}`
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [activeIndex, setActiveIndex] = useState(0)
  const [recents, setRecents] = useState(() => readRecents())

  useOutsideClick(
    rootRef,
    () => {
      setOpen(false)
      setQuery('')
    },
    open
  )

  const allFlat = useMemo(() => flattenOptions(categories), [categories])

  const labelByValue = useMemo(() => {
    const map = new Map()
    for (const item of allFlat) map.set(item.value, item.label)
    return map
  }, [allFlat])

  const selectedLabel = includeAll && value === allValue ? allLabel : (labelByValue.get(value) || '')

  const filteredFlat = useMemo(() => {
    const q = normalizeText(query)
    if (!q) return allFlat
    return allFlat.filter((o) => o.search.includes(q))
  }, [allFlat, query])

  const recentItems = useMemo(() => {
    if (!showRecents || includeAll) return []
    if (query) return []
    const idx = new Map(allFlat.map((o) => [o.value, o]))
    return recents.map((v) => idx.get(v)).filter(Boolean)
  }, [allFlat, includeAll, query, recents, showRecents])

  const grouped = useMemo(() => {
    const groups = []
    if (includeAll) {
      groups.push({
        name: 'Filtro',
        options: [
          { category: 'Filtro', value: allValue, label: allLabel, search: normalizeText(allLabel) }
        ]
      })
    }

    if (recentItems.length) {
      groups.push({ name: 'Recientes', options: recentItems })
    }

    const byCat = new Map()
    for (const item of filteredFlat) {
      const key = item.category || 'Otros'
      if (!byCat.has(key)) byCat.set(key, [])
      byCat.get(key).push(item)
    }

    for (const [name, options] of byCat.entries()) {
      groups.push({ name, options })
    }

    return groups
  }, [allLabel, allValue, filteredFlat, includeAll, recentItems])

  const flatVisible = useMemo(() => {
    const out = []
    for (const group of grouped) {
      for (const opt of group.options) out.push(opt)
    }
    return out
  }, [grouped])

  const indexByValue = useMemo(() => {
    const map = new Map()
    flatVisible.forEach((opt, idx) => map.set(opt.value, idx))
    return map
  }, [flatVisible])

  useEffect(() => {
    if (!open) return
    setActiveIndex(0)
    const t = window.setTimeout(() => searchRef.current?.focus(), 0)
    return () => window.clearTimeout(t)
  }, [open])

  const commit = (nextValue) => {
    onChange?.(nextValue)
    setOpen(false)
    setQuery('')

    if (!includeAll && nextValue) {
      setRecents((prev) => {
        const unique = [nextValue, ...prev.filter((v) => v !== nextValue)].slice(0, 8)
        writeRecents(unique)
        return unique
      })
    }
  }

  const onKeyDown = (e) => {
    if (e.key === 'Escape') {
      e.preventDefault()
      setOpen(false)
      setQuery('')
      return
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setActiveIndex((idx) => Math.min(flatVisible.length - 1, idx + 1))
      return
    }

    if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActiveIndex((idx) => Math.max(0, idx - 1))
      return
    }

    if (e.key === 'Enter') {
      e.preventDefault()
      const opt = flatVisible[activeIndex]
      if (opt) commit(opt.value)
    }
  }

  const currentCount = flatVisible.length
  const showEmpty = !loading && open && currentCount === 0

  return (
    <div ref={rootRef} className={className}>
      {label && (
        <label className="block text-sm font-bold text-gray-900 mb-1">
          {label} {required ? <span className="text-red-500">*</span> : null}
        </label>
      )}

      <button
        type="button"
        onClick={() => !disabled && setOpen((v) => !v)}
        disabled={disabled}
        className={`input flex items-center justify-between gap-3 text-left ${inputClassName} ${
          disabled ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : 'bg-white'
        }`}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className={`truncate ${selectedLabel ? 'text-gray-900' : 'text-gray-400'}`}>
          {selectedLabel || placeholder}
        </span>
        <svg className={`w-5 h-5 flex-shrink-0 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open && (
        <div className="relative">
          <div className="absolute z-50 mt-2 w-full rounded-xl border border-gray-200 bg-white shadow-lg overflow-hidden">
            <div className="p-3 border-b border-gray-100 bg-gray-50">
              <div className="flex items-center gap-2">
                <div className="relative flex-1">
                  <svg className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  <input
                    ref={searchRef}
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onKeyDown={onKeyDown}
                    className="w-full pl-9 pr-9 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-social-500 focus:border-transparent text-sm"
                    placeholder="Escribí para buscar..."
                    autoComplete="off"
                    inputMode="search"
                    role="combobox"
                    aria-label="Buscar tipo de asistencia"
                    aria-expanded={open}
                    aria-controls={listboxId}
                    aria-activedescendant={flatVisible[activeIndex] ? optionId(activeIndex) : undefined}
                  />
                  {query ? (
                    <button
                      type="button"
                      onClick={() => {
                        setQuery('')
                        searchRef.current?.focus()
                      }}
                      className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-md text-gray-500 hover:bg-gray-100"
                      aria-label="Limpiar"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  ) : null}
                </div>
                <span className="text-[11px] text-gray-500 whitespace-nowrap">{loading ? 'Cargando...' : `${currentCount}`}</span>
              </div>
            </div>

            <div className="max-h-72 overflow-auto">
              {loading ? (
                <div className="p-4 text-sm text-gray-500">Cargando tipos...</div>
              ) : showEmpty ? (
                <div className="p-4 text-sm text-gray-500">No hay resultados para "{query.trim()}".</div>
              ) : (
                <div className="py-2" role="listbox" id={listboxId} aria-label={label || 'Tipo de asistencia'}>
                  {grouped.map((group) => (
                    <div key={group.name} role="group" aria-label={group.name}>
                      <div className="px-3 py-1.5 text-[11px] font-black text-slate-700 uppercase tracking-wider bg-slate-50 sticky top-0 border-b border-slate-100">
                        {group.name}
                      </div>
                      <div className="px-2 pb-2">
                        {group.options.map((opt) => {
                          const idx = indexByValue.get(opt.value) ?? -1
                          const active = idx === activeIndex
                          const selected = opt.value === value
                          return (
                            <button
                              key={`${group.name}-${opt.value}`}
                              id={optionId(idx)}
                              type="button"
                              role="option"
                              aria-selected={selected}
                              onClick={() => commit(opt.value)}
                              onMouseEnter={() => setActiveIndex(idx)}
                              className={`w-full flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg text-left text-sm transition ${
                                active ? 'bg-social-50 border border-social-200' : 'hover:bg-gray-50'
                              } ${selected ? 'font-bold text-social-900 bg-social-50' : 'text-gray-900 font-medium'}`}
                            >
                              <span className="truncate">{opt.label}</span>
                              {selected ? (
                                <svg className="w-4 h-4 text-social-700 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                </svg>
                              ) : null}
                            </button>
                          )
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="px-3 py-2 border-t border-gray-100 bg-white flex items-center justify-between text-[11px] text-gray-500">
              <span>Atajos: ↑ ↓ Enter Esc</span>
              <button
                type="button"
                onClick={() => {
                  setOpen(false)
                  setQuery('')
                }}
                className="text-social-700 font-semibold hover:text-social-800"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
